import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { opsApi } from '@/api/ops';
import type { PackSummary } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select, Segmented } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { CheckboxField, ChoiceChips, ReasonField, TextAreaField, TextField, splitList, toggleIn } from '@/components/ops/Forms';
import { PageHeader, Section } from '@/components/ops/Layout';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatCredits } from '@/ops/credits';
import { REPLICA } from '@/ops/live';
import { stepName } from '@/ops/stepNames';
import { scaleUnavailableReason, useCreditScale } from '@/ops/useCreditScale';
import { useEnvironments, useSelectedEnvironment } from '@/ops/useEnvironment';
import {
  ADVANCED_FIELDS,
  INITIAL_WIZARD,
  buildJobBody,
  formProblems,
  grantOverrides,
  hostError,
  setupSummary,
  suggestDbShort,
  type FormField,
  type WizardState,
} from '@/ops/wizard';

/** The plan catalog is global in IAM; its route carries a tenant id only for symmetry (docs §7). */
const ANY_TENANT = '00000000-0000-0000-0000-000000000000';

const OWNER_EMAIL_TAKEN_TEXT = 'This email already has an account. Use another email for the owner.';

/** A pack section in words (the step it becomes, named). */
const sectionName = (section: string) => stepName(`industry.${section}`);

/**
 * Create a tenant (2026-09-30 redesign): ONE page. Four things are asked — name, owner e-mail, industry, plan — and
 * everything else sits under "Advanced" with a safe default (the selected environment, a database name from the tenant
 * name, an invitation instead of a password, the plan's apps, no own web address, the plan's and pack's credits). The
 * page ends with what will happen, in sentences. Submitting creates the same durable job as before — the tenant id is
 * minted ONCE by the console and every retry re-sends it — and opens its progress page.
 */
export function ProvisioningWizardPage() {
  const navigate = useNavigate();
  const environments = useEnvironments();
  const selectedEnv = useSelectedEnvironment().env;
  // `?kind=external` (the External accounts page's "Add") starts on an external account.
  const [params] = useSearchParams();
  const startExternal = params.get('kind') === 'external';
  const [state, setState] = useState<WizardState>(() => (startExternal ? { ...INITIAL_WIZARD, kind: 'external' } : INITIAL_WIZARD));
  const [dbShortTouched, setDbShortTouched] = useState(false);
  const [appsTouched, setAppsTouched] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<FormField>>(() => new Set());
  const [attempted, setAttempted] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(startExternal);
  const [planInfo, setPlanInfo] = useState<{ name: string; apps?: string[] } | null>(null);

  const envList = environments.data?.environments ?? [];
  const usable = (key: string | null) => !!key && envList.some((e) => e.key === key && e.enabled);
  // The environment follows the top bar's picker until the admin picks one here.
  useEffect(() => {
    if (!state.environment && envList.length > 0) {
      const fallback = usable(selectedEnv) ? selectedEnv! : envList.find((e) => e.enabled)?.key ?? '';
      if (fallback) setState((prev) => ({ ...prev, environment: fallback }));
    }
  }, [envList.length, selectedEnv]);

  const scale = useCreditScale(state.environment || null);
  const problems = formProblems(state, scale.scale);
  const problemOf = (field: FormField) =>
    attempted || touched.has(field) ? problems.find((p) => p.field === field)?.message ?? null : null;

  const set = <K extends keyof WizardState>(key: K, value: WizardState[K], field?: FormField) => {
    setState((prev) => ({ ...prev, [key]: value }));
    if (field) setTouched((prev) => (prev.has(field) ? prev : new Set(prev).add(field)));
  };

  const packs = useQuery({ queryKey: ['ops', 'packs'], queryFn: opsApi.packs.list, ...REPLICA, staleTime: 5 * 60_000 });
  const pack = packs.data?.packs.find((p) => p.key === state.industryPack) ?? null;
  const env = envList.find((e) => e.key === state.environment) ?? null;

  const create = useMutation({
    mutationFn: () => opsApi.provisioning.createJob(buildJobBody(state, scale.scale)),
    onSuccess: (job) => navigate(`/ops/provisioning/jobs/${job.id}`),
  });

  // The owner e-mail already has an account (IAM's answer, checked before any job exists): said on the field itself, and
  // gone as soon as the admin edits the address.
  const ownerEmailTaken = create.error instanceof ApiError && create.error.code === 'OWNER_EMAIL_TAKEN';

  const existingJobId =
    create.error instanceof ApiError && create.error.code === 'PROVISIONING_JOB_EXISTS' &&
    create.error.problem?.data && typeof create.error.problem.data === 'object' && 'job_id' in create.error.problem.data
      ? String((create.error.problem.data as { job_id: unknown }).job_id)
      : null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (problems.length > 0) {
      if (problems.some((p) => ADVANCED_FIELDS.has(p.field))) setAdvancedOpen(true);
      return;
    }
    create.mutate();
  };

  const summary = setupSummary(state, {
    environment: env?.label,
    pack: pack?.label?.en ?? null,
    // Sample records follow the checkbox, so they are said once — not also as a pack part.
    packParts: pack?.sections.filter((section) => section !== 'records').map(sectionName),
    plan: planInfo?.name ?? null,
    scale: scale.scale,
  });
  const tavi = state.kind === 'tavi';

  return (
    <RequirePermission permission={PERMISSIONS.tenantProvision} what="Creating a tenant">
      <PageHeader back={{ to: '/ops/provisioning', label: 'Setup jobs' }} title="Create a tenant"
        subtitle="Four things are needed. Everything else has a safe default under Advanced." />

      <QueryState query={environments}>
        {(envData) => (
          <form className="stack" onSubmit={submit} noValidate>
            <Section title="The basics">
              <div className="stack">
                <div className="form-grid">
                  <TextField label="Tenant name" value={state.label} required placeholder="Nile Grand Hotel"
                    onChange={(v) => {
                      set('label', v, 'label');
                      if (!dbShortTouched) set('dbShort', suggestDbShort(v));
                    }}
                    error={problemOf('label')}
                    hint="Shown to the owner and their customers (the chat brand and widget use it)." />
                  {tavi && (
                    <TextField label="Owner's e-mail" type="email" value={state.founderEmail} required placeholder="owner@example.com"
                      onChange={(v) => {
                        set('founderEmail', v, 'founderEmail');
                        if (ownerEmailTaken) create.reset();
                      }}
                      error={problemOf('founderEmail') ?? (ownerEmailTaken ? OWNER_EMAIL_TAKEN_TEXT : null)}
                      hint="They get an invitation to set their own password." />
                  )}
                </div>

                {tavi && (
                  <div className="field">
                    <span className="field-label t-label">Industry *</span>
                    <PackPicker packs={packs} value={state.industryPack} onChange={(v) => set('industryPack', v, 'industryPack')} />
                    {problemOf('industryPack') && <span className="field-hint t-caption note-error">{problemOf('industryPack')}</span>}
                    {state.industryPack !== 'none' && (
                      <CheckboxField label="Add sample records" checked={state.sampleRecords}
                        onChange={(v) => set('sampleRecords', v)}
                        hint="A few made-up records (example.com addresses, fictional phone numbers) so the owner sees the CRM working. They can be deleted any time and never come back." />
                    )}
                  </div>
                )}

                <PlanPicker env={state.environment} value={state.planCode} error={problemOf('planCode')}
                  onChange={(code, info) => {
                    set('planCode', code, 'planCode');
                    setPlanInfo(info);
                    if (!appsTouched && info?.apps) set('apps', info.apps);
                  }} />
              </div>
            </Section>

            <details className="card advanced" open={advancedOpen} onToggle={(e) => setAdvancedOpen((e.target as HTMLDetailsElement).open)}>
              <summary className="card-header">
                <span className="t-heading">Advanced</span>
                <span className="t-caption muted advanced-defaults">
                  {[
                    env?.label ?? 'no environment',
                    `database iam_t_${state.dbShort || '…'}`,
                    tavi ? (state.generatePassword ? 'generated password' : 'invitation') : 'external account',
                    tavi ? (state.hosts.length ? `${state.hosts.length} web address(es)` : 'no own web address') : null,
                    "plan's credits",
                  ].filter(Boolean).join(' · ')}
                </span>
              </summary>
              <div className="card-body stack">
                <Segmented label="Kind of account" value={state.kind} onChange={(k) => set('kind', k)}
                  options={[{ value: 'tavi', label: 'Tenant (CRM, chat, email…)' }, { value: 'external', label: 'External account (SMS / payments API only)' }]} />

                <div className="form-grid">
                  <Select
                    label="Environment"
                    value={state.environment}
                    onChange={(v) => set('environment', v, 'environment')}
                    placeholder="Choose where the tenant lives"
                    options={envData.environments.map((e) => ({
                      value: e.key,
                      label: e.label || e.key,
                      hint: !e.enabled ? 'switched off' : !e.has_internal_api_key ? 'no internal key yet' : undefined,
                      disabled: !e.enabled,
                    }))}
                    hint={problemOf('environment') ?? 'Defaults to the environment picked in the top bar.'}
                  />
                  <TextField label="Database name" value={state.dbShort} mono required
                    onChange={(v) => { setDbShortTouched(true); set('dbShort', v, 'dbShort'); }}
                    error={problemOf('dbShort')}
                    hint={`Becomes iam_t_${state.dbShort || '…'} — made from the tenant name; it can never be renamed.`} />
                  {tavi && (
                    <Segmented label="Use" value={state.useType} onChange={(v) => set('useType', v)}
                      options={[{ value: 'team', label: 'A team' }, { value: 'personal', label: 'One person' }]} />
                  )}
                </div>

                {tavi && (
                  <>
                    <div className="form-grid">
                      <TextField label="Owner's first name" value={state.founderFirstName} onChange={(v) => set('founderFirstName', v)} />
                      <TextField label="Owner's last name" value={state.founderLastName} onChange={(v) => set('founderLastName', v)} />
                    </div>
                    <Segmented label="How the owner signs in the first time" value={state.generatePassword ? 'generate' : 'invite'}
                      onChange={(v) => set('generatePassword', v === 'generate')}
                      options={[{ value: 'invite', label: 'Invitation e-mail (recommended)' }, { value: 'generate', label: 'Generate a password' }]} />
                    <p className="t-caption muted" style={{ margin: 0 }}>
                      {state.generatePassword
                        ? 'A password is generated and kept encrypted until an owner of this console reveals it once on the progress page. You hand it over yourself.'
                        : 'The owner gets an e-mail to set their own password; no password passes through the console.'}
                    </p>

                    <AppsPicker env={state.environment} planCode={state.planCode} value={state.apps} error={problemOf('apps')}
                      onChange={(apps) => { setAppsTouched(true); set('apps', apps, 'apps'); }} />

                    <HostsPicker hosts={state.hosts} onChange={(hosts) => set('hosts', hosts, 'hosts')}
                      suggestion={env?.public_host_suffix && state.dbShort ? `${state.dbShort}.${env.public_host_suffix}` : null} />
                  </>
                )}

                <div className="stack-sm">
                  <span className="field-label t-label">Starting credits</span>
                  <p className="t-caption muted" style={{ margin: 0 }}>
                    Leave empty for the plan&apos;s AI credits and the pack&apos;s SMS/WhatsApp credits. Typed in credits as the tenant sees
                    them; converted with billing&apos;s live scale, never a guessed one.
                  </p>
                  {scale.status !== 'ok' && state.environment && (
                    <div className="callout callout-warning" role="status">
                      <span className="callout-icon" aria-hidden="true">!</span>
                      <div className="t-body">{scaleUnavailableReason(scale.status)} The fields are off; the setup still uses the plan and pack defaults.</div>
                    </div>
                  )}
                  <div className="form-grid">
                    {tavi && (
                      <TextField label="AI credits" value={state.aiCredits} onChange={(v) => set('aiCredits', v, 'grants')}
                        disabled={scale.status !== 'ok'} hint="0 = no AI credits." />
                    )}
                    <TextField label="SMS/WhatsApp credits" value={state.messagingCredits} onChange={(v) => set('messagingCredits', v, 'grants')}
                      disabled={scale.status !== 'ok'} />
                  </div>
                  <GrantPreview state={state} scale={scale.scale} />
                  {(state.aiCredits.trim() || state.messagingCredits.trim()) && (
                    <ReasonField value={state.grantReason} onChange={(v) => set('grantReason', v, 'grants')}
                      label="Why these amounts (required above zero — recorded on the audit trail)" />
                  )}
                  {problemOf('grants') && <span className="t-caption note-error">{problemOf('grants')}</span>}
                </div>
              </div>
            </details>

            <Section title="What will happen">
              <ol className="summary-list t-body">
                {summary.map((line) => <li key={line}>{line}</li>)}
              </ol>
              {attempted && problems.length > 0 && (
                <div className="callout callout-error" role="alert" style={{ marginTop: 12 }}>
                  <span className="callout-icon" aria-hidden="true">!</span>
                  <div className="t-body">
                    <strong className="t-heading" style={{ display: 'block' }}>Fix {problems.length === 1 ? 'this' : 'these'} first</strong>
                    <ul className="problem-list">
                      {problems.map((p) => <li key={p.field}>{FIELD_NAMES[p.field]}: {p.message}</li>)}
                    </ul>
                  </div>
                </div>
              )}
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="submit" className="btn btn-primary" disabled={create.isPending}>
                  {create.isPending ? 'Starting…' : 'Create the tenant'}
                </button>
                <Link to="/ops/provisioning" className="btn btn-ghost">Cancel</Link>
              </div>
              <div style={{ marginTop: 12 }}>
                <MutationError error={create.error} />
                {existingJobId && (
                  <p className="t-body" style={{ margin: '8px 0 0' }}>
                    <Link to={`/ops/provisioning/jobs/${existingJobId}`}>Open the setup that already exists</Link> and retry it instead.
                  </p>
                )}
              </div>
              <details className="t-caption" style={{ marginTop: 12 }}>
                <summary>Technical details (the request)</summary>
                <pre className="pre-json">{JSON.stringify(buildJobBody(state, scale.scale), null, 2)}</pre>
              </details>
            </Section>
          </form>
        )}
      </QueryState>
    </RequirePermission>
  );
}

const FIELD_NAMES: Record<FormField, string> = {
  label: 'Tenant name',
  founderEmail: "Owner's e-mail",
  industryPack: 'Industry',
  planCode: 'Plan',
  environment: 'Environment (Advanced)',
  dbShort: 'Database name (Advanced)',
  apps: 'Apps (Advanced)',
  hosts: 'Web addresses (Advanced)',
  grants: 'Starting credits (Advanced)',
};

function GrantPreview({ state, scale }: { state: WizardState; scale: number | null }) {
  const grants = grantOverrides(state, scale);
  if (!grants.ok) return <span className="t-caption note-error">{grants.error}</span>;
  if (!grants.value) return null;
  return (
    <span className="t-caption muted">
      Will give{' '}
      {Object.entries(grants.value).map(([k, v], i) => (
        <span key={k}>{i > 0 && ', '}{formatCredits(v, scale)} {k === 'ai_credits' ? 'AI' : 'SMS/WhatsApp'}</span>
      ))}.
    </span>
  );
}

function PackPicker({
  packs, value, onChange,
}: {
  packs: ReturnType<typeof useQuery<{ packs: PackSummary[] }>>;
  value: string;
  onChange: (key: string) => void;
}) {
  return (
    <QueryState query={packs}>
      {(data) => (
        <div className="pack-grid" role="radiogroup" aria-label="Industry">
          {data.packs.filter((p) => !p.retired).map((p) => <PackOption key={p.key} pack={p} selected={value === p.key} onSelect={() => onChange(p.key)} />)}
          <button type="button" role="radio" aria-checked={value === 'none'} className={`pack-option${value === 'none' ? ' is-selected' : ''}`}
            onClick={() => onChange('none')}>
            <strong className="t-heading">No industry</strong>
            <span className="t-caption muted">The plain platform set-up — no industry fields, chat menu, templates or agents.</span>
          </button>
        </div>
      )}
    </QueryState>
  );
}

function PackOption({ pack, selected, onSelect }: { pack: PackSummary; selected: boolean; onSelect: () => void }) {
  return (
    <button type="button" role="radio" aria-checked={selected} disabled={!pack.valid}
      className={`pack-option${selected ? ' is-selected' : ''}`} onClick={onSelect}
      title={pack.valid ? pack.sections.map(sectionName).join(' · ') : pack.errors.join('\n')}>
      <strong className="t-heading">{pack.label?.en ?? pack.key}</strong>
      {pack.label?.ar && <span className="t-caption muted" dir="rtl">{pack.label.ar}</span>}
      <span className="t-caption muted">{pack.description?.en ?? ''}</span>
      {!pack.valid && <StatusChip status="invalid" tone="danger" />}
    </button>
  );
}

function PlanPicker({
  env, value, onChange, error,
}: {
  env: string;
  value: string;
  onChange: (code: string, info: { name: string; apps?: string[] } | null) => void;
  error: string | null;
}) {
  const canRead = hasPermission(PERMISSIONS.subscriptionRead);
  const plans = useQuery({
    queryKey: ['ops', 'plans', env],
    queryFn: () => opsApi.subscription.plans(env, ANY_TENANT),
    enabled: !!env && canRead,
    retry: false,
    staleTime: 15 * 60_000,
  });

  if (plans.data && plans.data.length > 0) {
    return (
      <Select label="Plan *" value={value} placeholder="Choose a plan"
        onChange={(code) => {
          const plan = plans.data!.find((p) => p.plan_code === code);
          onChange(code, plan ? { name: plan.display_name, apps: plan.apps } : null);
        }}
        options={plans.data.map((p) => ({
          value: p.plan_code,
          label: p.display_name,
          hint: p.ai_credit_grant === null ? 'unlimited AI' : `${p.ai_credit_grant.toLocaleString('en-US')} AI credits`,
        }))}
        hint={error ?? 'Sets the apps and the starting AI credits.'} />
    );
  }

  return (
    <div className="stack-sm">
      {plans.isError && <MutationError error={plans.error} />}
      {!canRead && <span className="t-caption muted">Your role cannot read the plan list — type the plan code.</span>}
      {!env && <span className="t-caption muted">Choose an environment (under Advanced) to see its plans.</span>}
      <TextField label="Plan code" value={value} onChange={(v) => onChange(v, null)} mono required placeholder="growth" error={error} />
    </div>
  );
}

function AppsPicker({
  env, planCode, value, onChange, error,
}: {
  env: string;
  planCode: string;
  value: string[];
  onChange: (apps: string[]) => void;
  error: string | null;
}) {
  const plans = useQuery({
    queryKey: ['ops', 'plans', env],
    queryFn: () => opsApi.subscription.plans(env, ANY_TENANT),
    enabled: !!env && hasPermission(PERMISSIONS.subscriptionRead),
    retry: false,
    staleTime: 15 * 60_000,
  });
  const known = useMemo(() => {
    const set = new Set<string>(value);
    for (const p of plans.data ?? []) for (const a of p.apps) set.add(a);
    return [...set].sort();
  }, [plans.data, value]);
  const [extra, setExtra] = useState('');
  const planApps = plans.data?.find((p) => p.plan_code === planCode)?.apps;

  return (
    <>
      {known.length > 0 && (
        <ChoiceChips label="Apps" options={known.map((a) => ({ value: a }))} selected={value} onToggle={(a) => onChange(toggleIn(value, a))}
          hint={planApps ? `The plan includes: ${planApps.join(', ')}.` : undefined} />
      )}
      <div className="row-end">
        <div style={{ flex: '1 1 240px' }}>
          <TextField label={known.length > 0 ? 'Add other apps' : 'Apps'} value={extra} onChange={setExtra} mono placeholder="crm, salesiq, mail"
            error={error} />
        </div>
        <button type="button" className="btn btn-ghost btn-sm" disabled={!extra.trim()}
          onClick={() => { onChange([...new Set([...value, ...splitList(extra).map((a) => a.toLowerCase())])]); setExtra(''); }}>
          Add
        </button>
      </div>
    </>
  );
}

function HostsPicker({ hosts, onChange, suggestion }: { hosts: string[]; onChange: (hosts: string[]) => void; suggestion: string | null }) {
  const [text, setText] = useState(hosts.join('\n'));
  const list = splitList(text);
  const errors = list.map(hostError).filter((e): e is string => e !== null);

  return (
    <>
      <TextAreaField label="Own web addresses (optional)" value={text} mono rows={3}
        onChange={(v) => { setText(v); onChange(splitList(v).map((h) => h.toLowerCase())); }}
        error={errors[0] ?? (list.length > 20 ? 'At most 20 hosts.' : null)}
        hint="Host names only (no https://, port or path), one per line. Without one, people sign in on the main address." />
      {suggestion && !list.includes(suggestion) && (
        <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
          onClick={() => { const next = [...list, suggestion]; setText(next.join('\n')); onChange(next); }}>
          Add {suggestion}
        </button>
      )}
    </>
  );
}
